import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Menu } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { findRouteKeyFromPath, getLocalizedPath, languageFromPath, type RouteKey } from '@/i18n/routes'
import { publicCategories } from '@/data/public-catalog'
import { contentRoutes } from '@/i18n/content-routes'
import { useOverlay } from './OverlayProvider'
import { ShellDialog } from './ShellDialog'

export function Header() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const lang = languageFromPath(location.pathname)
  const { active, open, close } = useOverlay()
  const [scrolled, setScrolled] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [mobileCategories, setMobileCategories] = useState(false)
  const categoryRef = useRef<HTMLDivElement>(null)
  const categoryTrigger = useRef<HTMLButtonElement>(null)
  const normalizedPath = location.pathname.replace(/\/$/, '')
  const homeHeader = normalizedPath === getLocalizedPath('home', lang)
  const routeKey = findRouteKeyFromPath(normalizedPath.split('/').slice(2).join('/'), lang)
  const contentRoute = contentRoutes.some((record) => record[lang] === normalizedPath)
  const internalHeader = !homeHeader && Boolean(routeKey || contentRoute)
  const catalogActive = location.pathname === getLocalizedPath('solutions', lang) || contentRoutes.some((record) => record.type !== 'news' && record[lang] === location.pathname)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  useEffect(() => { setCategoriesOpen(false); setMobileCategories(false) }, [location.key])
  useEffect(() => { if (active) setCategoriesOpen(false) }, [active])
  useEffect(() => {
    const desktop = matchMedia('(min-width: 1024px)')
    const onChange = () => { if (desktop.matches && active === 'menu') close(); setCategoriesOpen(false) }
    desktop.addEventListener('change', onChange)
    return () => desktop.removeEventListener('change', onChange)
  }, [active, close])
  useEffect(() => {
    if (!categoriesOpen) return
    const outside = (event: PointerEvent) => { if (!categoryRef.current?.contains(event.target as Node)) setCategoriesOpen(false) }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { setCategoriesOpen(false); categoryTrigger.current?.focus() } }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape) }
  }, [categoriesOpen])
  const navLink = (key: RouteKey) => <LocalizedLink to={key} aria-current={(key === 'about' ? location.hash === '#chi-siamo' : location.pathname === getLocalizedPath(key, lang)) ? 'page' : undefined}>{t(`nav.${key}`)}</LocalizedLink>
  const categories = <ul className="category-links">{publicCategories.map((category) => <li key={category.id}><LocalizedLink to={category.id} aria-current={contentRoutes.find((record) => record.id === category.id)?.[lang] === location.pathname ? 'page' : undefined}>{t(`categories.${category.id}`)}</LocalizedLink></li>)}</ul>
  return <header className={`site-header${homeHeader ? ' site-header--home' : ''}${internalHeader ? ' site-header--internal' : ''}${scrolled ? ' is-scrolled' : ''}${active === 'menu' ? ' site-header--menu-open' : ''}`}>
    <div className="shell-container header-inner">
      <LocalizedLink to="home" className="brand-link" aria-label={t('aria.brandHome')}><img src="/brand/esamed-mark.png" width="152" height="152" alt="" /></LocalizedLink>
      <nav className="desktop-nav" aria-label={t('aria.mainNavigation')}>
        <div ref={categoryRef} className="catalog-navigation" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setCategoriesOpen(false) }}>
          <div className="catalog-navigation-label"><LocalizedLink to="solutions" aria-current={catalogActive ? 'page' : undefined}>{t('nav.solutions')}</LocalizedLink><button ref={categoryTrigger} className="icon-button" aria-label={t('aria.categories')} aria-expanded={categoriesOpen} aria-controls="desktop-categories" onClick={() => setCategoriesOpen(!categoriesOpen)}><ChevronDown aria-hidden="true" /></button></div>
          <div className="category-panel" id="desktop-categories" hidden={!categoriesOpen}>{categories}<LocalizedLink className="all-solutions" to="solutions">{t('nav.allSolutions')}</LocalizedLink></div>
        </div>
        {navLink('about')}{navLink('news')}{navLink('contacts')}
      </nav>
      <div className="desktop-language"><LanguageSelector /></div>
      <button className="icon-button mobile-trigger" onClick={() => open('menu')} aria-label={t('aria.openMenu')} aria-expanded={active === 'menu'} aria-controls="mobile-menu"><Menu aria-hidden="true" /></button>
    </div>
    <ShellDialog id="mobile-menu" open={active === 'menu'} onClose={close} title={t('nav.menu')} className="mobile-menu">
      <nav className="mobile-nav" aria-label={t('aria.mobileNavigation')}>
        <div className="mobile-catalog-label">{navLink('solutions')}<button className="icon-button" aria-label={t('aria.categories')} aria-expanded={mobileCategories} aria-controls="mobile-categories" onClick={() => setMobileCategories(!mobileCategories)}><ChevronDown aria-hidden="true" /></button></div>
        <div id="mobile-categories" hidden={!mobileCategories}>{categories}</div>
        {navLink('about')}{navLink('news')}{navLink('contacts')}
      </nav>
      <LanguageSelector variant="mobile" />
    </ShellDialog>
  </header>
}
